import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';

import { Heading } from '../../../../components/Heading';
import { FormSelect } from '../../../../components/forms/FormSelect';

import { sendAutomatedEmail } from '../../../../data/sendAutomatedEmail';
import { setCompletedPage } from '../../../../data/navigation/utilities/setCompletedPage';

import * as Sentry from '@sentry/browser';

import { FinalUpdate } from '../../../../types/Patient/FinalUpdate';
import { NeedsReview } from '../../../../types/Patient/NeedsReview';
import { Roles } from '../../../../types/Patient/Roles';
import { CardStatus } from '../../../../types/Patient/CardStatus';
import { ByLogo } from '../../../../types/Patient/ByLogo';

import { formatDateForDisplay } from '../../../../utilities/formatDateForDisplay';
import { formatPhoneNumber } from '../../../../utilities/formatPhoneNumber';

export const Route = createFileRoute('/_authenticated/$patientId/card/cardPreview')({
  component: CardPreviewPage,
});

function CardPreviewPage() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const { mutate: mutateChartDoc } = useUpdateChartSection();
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const [currentError, setCurrentError] = React.useState<string>('select');

  const potentialErrors = [
    { name: 'Select an error', value: 'select' },
    { name: 'My name, date of birth, and/or address is incorrect', value: 'patientInfo' },
    { name: 'My organ donor status is incorrect', value: 'organDonor' },
    { name: "My healthcare agent's information is incorrect", value: 'healthcareAgent' },
    { name: 'My medical order document information is incorrect', value: 'medicalOrder' },
  ];

  const handleErrorSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentError(e.target.value);
    switch (e.target.value) {
      case 'patientInfo':
        navigate({ to: `/${patientId}/info/patientInfo` });
        break;
      case 'organDonor':
        navigate({ to: `/${patientId}/info/check` });
        break;
      case 'healthcareAgent':
        navigate({ to: `/${patientId}/info/hcaInfo` });
        break;
      case 'medicalOrder':
        navigate({ to: `/${patientId}/upload/files` });
        break;
      default:
        break;
    }
  };

  const handleSignOut = async () => {
    // KICK OFF THE AUTOMATED "CARD IN REVIEW" EMAIL
    sendAutomatedEmail({
      type: 'cardInReview',
      patientId: patientId,
      email: patientInfo?.contactEmail,
      name: `${chart?.PATIENT_INFO_1} ${chart?.PATIENT_INFO_1.lastName}`,
    });
    // MARK FINAL SIGNATURES PAGE AS COMPLETED
    await setCompletedPage({ name: 'finalSignatures', value: true, patientInfo: patientInfo })
      .then(async () => {
        // UPDATE DISPLAY NAME TO SHOW PATIENT NAME IN WEB CONSOLE
        const displayName = `${chart.PATIENT_INFO_1.firstName} ${chart.PATIENT_INFO_1.middleInitial}. ${chart.PATIENT_INFO_1.lastName}`;
        mutatePatientDoc(
          {
            patientId: patientId,
            displayName: displayName,
          },
          {
            onSuccess: () => {
              // alert('Successfully updated document');
            },
            onError: (error) => {
              Sentry.captureException(error);
              alert(`Error updating document: ${error.message}`); // TELL THE USER
              console.error(error); // GET THE STACKTRACE
            },
          },
        );
      })
      .then(async () => {
        if (!chart.OTHER_DOCUMENTS) {
          mutateChartDoc(
            {
              patientId: patientId,
              docData: {
                OTHER_DOCUMENTS: {},
              },
            },
            {
              onSuccess: () => {
                // alert("Successfully updated document");
              },
              onError: (error) => {
                Sentry.captureException(error);
                alert(`Error updating document: ${error.message}`); // TELL THE USER
                console.error(error); // GET THE STACKTRACE
              },
            },
          );
        }
      })
      .then(async () => {
        // UPDATE FILTER OPTIONS TO SHOW FULL CODE
        let filterOptionsClone = structuredClone(patientInfo.filterOptions);
        if (!filterOptionsClone) {
          filterOptionsClone = {
            code: chart.SELECTED_TREATMENT_OPTION?.code ? chart.SELECTED_TREATMENT_OPTION.code : 'fullCode',
            createdOn: Date.now(),
            isArchived: false,
          };
        } else {
          filterOptionsClone.code = chart.SELECTED_TREATMENT_OPTION?.code
            ? chart.SELECTED_TREATMENT_OPTION.code
            : 'fullCode';
          filterOptionsClone.createdOn = Date.now();
          filterOptionsClone.isArchived = false;
        }

        const dataObj = {
          filterOptions: filterOptionsClone,
          applicationStatus: 'submitted',
          byLogo: {
            active: true,
            createdAt: {
              seconds: Date.now() / 1000,
              nanoseconds: 0,
            },
            createdBy: 'system',
            defaultLogo: true,
            downloadUri: `${import.meta.env.VITE_PUBLIC_STORAGE_URL}/logos/defaultByLogo.png`,
            id: 'defaultByLogo.png',
            storagePath: 'logos',
          } as ByLogo,
          secondaryLogo: {
            active: true,
            createdAt: {
              seconds: Date.now() / 1000,
              nanoseconds: 0,
            },
            createdBy: 'system',
            defaultLogo: true,
            downloadUri: `${import.meta.env.VITE_PUBLIC_STORAGE_URL}/logos/defaultSecondaryLogo.png`,
            id: 'defaultSecondaryLogo.png',
            storagePath: 'logos',
          } as ByLogo,
          roles: {
            isPatient: true,
            orgAdmin: false,
            OrgQC: false,
            siteAdmin: false,
            siteQC: false,
            acpe: false,
            provider: false,
          } as Roles,
          needsReview: {
            flagged: false,
            flaggedByName: '',
            flaggedByUID: '',
            flaggedReason: '',
            flaggedTime: 0,
            uid: '',
            unflaggedByUID: '',
          } as NeedsReview,
          isArchived: false,
          cardStatus: {
            cardPrinted: {
              description: 'Text verified',
              isComplete: false,
              sortOrder: 5,
            },
            codeCorrect: {
              description: 'Confirmed QR code links to correct video.',
              isComplete: false,
              sortOrder: 6,
            },
            imageCorrect: {
              description: 'Patient photo Verified',
              isComplete: false,
              sortOrder: 2,
            },
            releaseApproved: {
              description: 'Card released',
              isComplete: false,
              sortOrder: 7,
            },
            textVerified: {
              description: 'Card text verified',
              isComplete: false,
              sortOrder: 1,
            },
            videoProcessed: {
              description: 'Video processed',
              isComplete: false,
              sortOrder: 4,
            },
            videoVerified: {
              description: 'Video verified',
              isComplete: false,
              sortOrder: 3,
            },
          } as CardStatus,
        };
        mutatePatientDoc(
          {
            patientId: patientId,
            data: dataObj,
          } as FinalUpdate,
          {
            onSuccess: () => {
              // alert('Successfully updated document');
            },
            onError: (error) => {
              Sentry.captureException(error);
              alert(`Error updating document: ${error.message}`); // TELL THE USER
              console.error(error); // GET THE STACKTRACE
            },
          },
        );
      })
      .then(() => {
        mutateChartDoc(
          {
            patientId: patientId,
            docData: {
              SELECTED_TREATMENT_OPTION: {
                code: 'fullCode',
                description: 'Full Code - All Treatment & No Limitations in Care',
              },
            },
          },
          {
            onSuccess: () => {
              // alert("Successfully updated document");
            },
            onError: (error) => {
              Sentry.captureException(error);
              alert(`Error updating document: ${error.message}`); // TELL THE USER
              console.error(error); // GET THE STACKTRACE
            },
          },
        );
      })
      .then(async () => {
        window.localStorage.removeItem('emailForSignIn');
      });
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <div id="card-preview-page" className="flex w-full flex-col">
        <div id="card-preview-page-header" className="flex flex-col">
          <Heading id="card-preview" textContent="Card Preview" />
          <p className="text-center text-2xl @sm:text-sm">
            Below is a preview of your upcoming card (front/back).Please review for accuracy.
          </p>
        </div>
        <div id="card-preview-container" className="flex flex-col items-center justify-center">
          <div
            id="card-front"
            className="i bg-true-white @container my-4 aspect-[1.586] w-[300px] flex-col rounded-xl outline-solid sm:w-[540px]"
          >
            <div id="card-front-header" className="bg-mideo-darkred">
              <p className="p-2 text-xs text-white sm:text-xl">
                MIDEO<sup>{String.fromCharCode(0x000a9)}</sup> - My Informed Decision on VidEO
                {String.fromCharCode(0x02122)}
              </p>
            </div>
            <div id="card-front-body-container" className="flex flex-row">
              <div id="card-front-body-left" className="flex flex-col pt-2 pl-2 text-[0.45rem] sm:text-base">
                <div id="patient-name" className="text-sm font-bold sm:text-2xl">
                  <p>
                    {`${chart.PATIENT_INFO_1.firstName} `}
                    {chart.PATIENT_INFO_1.middleInitial ? `${chart.PATIENT_INFO_1.middleInitial}. ` : ' '}
                    {`${chart.PATIENT_INFO_1.lastName}`}
                  </p>
                </div>
                <div id="code-status" className="flex flex-row">
                  <div id="status-title" className="w-3/12">
                    <p>Code Status: </p>
                  </div>
                  <div id="status-text" className="w-9/12">
                    <p>
                      {chart?.SELECTED_TREATMENT_OPTION?.description
                        ? chart.SELECTED_TREATMENT_OPTION.description
                        : 'Full Code - All Treatment & No Limitations in Care'}
                    </p>
                  </div>
                </div>
                <div id="organ-donor" className="flex flex-row">
                  <div id="organ-donor-title">
                    <p>Organ Donor:</p>
                  </div>
                  <div id="organ-donor-text" className="ml-2">
                    <p>{chart.LIVING_WILL.organDonor}</p>
                  </div>
                </div>
                <div id="living-will" className="t flex flex-row">
                  <p>Living Will:</p>
                  <p className="ml-2">{chart.OTHER_DOCUMENTS.livingWillExists === true ? 'Yes' : 'No'}</p>
                </div>
                <div id="health-care-agent-name" className="flex flex-row">
                  <div id="health-care-agent-title">
                    <p>Health Care Agent:</p>
                  </div>
                  <div id="health-care-agent-text" className="ml-2">
                    <p>{chart.HEALTHCARE_AGENT_INFO.agent}</p>
                  </div>
                </div>
                <div id="health-care-agent-phone" className="flex flex-row">
                  <div id="health-care-agent-phone-title">
                    <p>Phone:</p>
                  </div>
                  <div id="health-care-agent-phone-text" className="ml-2">
                    <p>{formatPhoneNumber(chart.HEALTHCARE_AGENT_INFO.cellPhone)}</p>
                  </div>
                </div>
                <div id="medical-order" className="t flex flex-row">
                  <p>{chart.PATIENT_INFO_2.state.toLowerCase().includes('md') ? `MOLST:` : `POLST:`}</p>
                  <p className="ml-2">{chart.OTHER_DOCUMENTS.POLSTExists === true ? 'Yes' : 'No'}</p>
                </div>
                {/* <div id="tap-to-scan-rfid">
                <p></p>
              </div> */}
                <div id="card-front-left-footer-" className="mt-4 flex flex-row items-center sm:mt-6">
                  <div id="front-logo">
                    <img
                      src="https://storage.googleapis.com/web-console-mideoid-public/logos/defaultByLogo.png"
                      alt="MIDEO Health Logo"
                      className="h-8 sm:h-14"
                    />
                  </div>
                  <div id="expiry-info" className="mx-2 text-[0.45rem] sm:text-base">
                    <p>ISSUED: YYYY-MM-DD</p>
                    <p>EXPIRES: YYYY-MM-DD</p>
                  </div>
                </div>
              </div>
              <div id="card-front-body-right" className="my-2 flex w-4/12 flex-col">
                <div id="patient-photo" className="flex flex-col items-center">
                  <img src={chart.PHOTO_FOR_CARD.portraitURI} alt="Patient" className="mr-2 h-14 sm:mr-4 sm:h-22" />
                </div>
                <div id="DOB-info" className="mr-2 flex flex-col items-center text-[0.4rem] sm:text-sm">
                  <p>DOB:</p>
                  <p>{formatDateForDisplay(chart.PATIENT_INFO_1.DOB)}</p>
                </div>
                <div id="front-qr-code" className="mr-4 sm:mt-8">
                  <img src="/sampleQR.png" alt="Sample QR Code" className="h-14 sm:h-28" />
                </div>
              </div>
            </div>
          </div>
          <div
            id="card-back"
            className="i bg-true-white @container my-4 flex aspect-[1.586] w-[300px] flex-col rounded-xl outline-solid sm:w-[540px]"
          >
            <div id="card-back-container" className="flex flex-row">
              <div id="card-back-left" className="ml-2 flex w-1/2 flex-col text-[0.5rem] sm:text-base">
                <div id="card-back-logo" className="mt-2">
                  <img
                    src="https://storage.googleapis.com/web-console-mideoid-public/logos/defaultSecondaryLogo.png"
                    alt="MIDEO Logo"
                    className="h-8 sm:h-14"
                  />
                </div>
                <div id="patient-sig-area" className="mt-0 sm:mt-2">
                  <img src={chart.PATIENT_SIGNATURE.signature1DataURI} alt="Patient Signature" className="h-4 sm:h-8" />
                  <p className="text-[.45rem] sm:text-[.7rem]">Patient Signature</p>
                  <p className="text-[.45rem] sm:text-[.7rem]">MM-DD-YYYY</p>
                </div>
                <div id="witness-sig-area" className="mt-0 sm:mt-2">
                  {chart.WITNESS_SIGNATURE.signature1DataURI ? (
                    <img
                      src={chart.WITNESS_SIGNATURE.signature1DataURI}
                      alt="Witness Signature"
                      className="h-4 sm:h-8"
                    />
                  ) : (
                    <p id="waiting" className="text-xs font-bold text-red-500">
                      AWAITING SIGNATURE
                    </p>
                  )}
                  <p className="text-[.45rem] sm:text-[.7rem]">Witness Signature</p>
                  <p className="text-[.45rem] sm:text-[.7rem]">MM-DD-YYYY</p>
                </div>
                <div id="witness-sig-2-area" className="mt-0 sm:mt-2">
                  {chart.WITNESS_SIGNATURE.signature2DataURI ? (
                    <img
                      src={chart.WITNESS_SIGNATURE.signature2DataURI}
                      alt="Witness Signature"
                      className="h-4 sm:h-8"
                    />
                  ) : (
                    <p id="waiting" className="text-xs font-bold text-red-500">
                      AWAITING SIGNATURE
                    </p>
                  )}
                  <p className="text-[.45rem] sm:text-[.7rem]">Witness Signature</p>
                  <p className="text-[.45rem] sm:text-[.7rem]">MM-DD-YYYY</p>
                </div>
                <div id="dr-sig-area" className="mt-0 sm:mt-2">
                  <p id="dr-sig" className="text-[.5rem] font-bold text-red-500 lg:text-[.75rem]">
                    SHOWN ON CARD
                  </p>
                  <p className="text-[.45rem] sm:text-[.7rem]">Physician Signature</p>
                  <p className="text-[.45rem] sm:text-[.7rem]">MM-DD-YYYY</p>
                </div>
              </div>
              <div id="card-back-right" className="flex w-1/2 flex-col">
                <div id="lost-info" className="mt-2 flex w-full flex-col items-end pr-4 text-[.5rem] sm:text-base">
                  <p id="if-lost">If lost, please contact</p>
                  <p id="patient-name">
                    {`${chart.PATIENT_INFO_1.firstName} `}
                    {chart.PATIENT_INFO_1.middleInitial ? `${chart.PATIENT_INFO_1.middleInitial}. ` : ' '}
                    {`${chart.PATIENT_INFO_1.lastName}`}
                  </p>
                  <p id="street-address">{chart.PATIENT_INFO_2.streetAddress}</p>
                  <p id="city-state-zip">{`${chart.PATIENT_INFO_2.city}, ${chart.PATIENT_INFO_2.state} ${chart.PATIENT_INFO_2.ZIP}`}</p>
                  <p id="alt-contact">Alternate Contact:</p>
                  <p id="see-front">See Healthcare Agent info on</p>
                  <p id="front">front of card.</p>
                </div>
                <div id="back-qr-code" className="mt-4 ml-12 sm:ml-28">
                  <img src="/sampleQR.png" alt="Sample QR Code" className="h-18 sm:h-32" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {patientInfo.applicationStatus === 'open' && (
          <div id="verification-area" className="flex flex-col items-center">
            <p className="text-center text-2xl italic">
              If you see any errors in the samples above, making a selection in the box below will take you to the page
              where you can fix the error. Make the changes and click the submit button to save them. Then use the
              browser&apos;s back button to return here.
            </p>
            <FormSelect
              label=""
              id="correct-errors"
              name="correct-errors"
              value={currentError}
              required={false}
              onBlur={() => {
                return;
              }}
              onChange={(e) => handleErrorSelection(e)}
              options={potentialErrors}
            />
          </div>
        )}
        {(patientInfo.applicationStatus === 'open' &&
          chart?.OTHER_DOCUMENTS?.livingWillExists &&
          chart?.OTHER_DOCUMENTS?.livingWillURI?.length === 0) ||
        (chart?.OTHER_DOCUMENTS?.POLSTExists && chart?.OTHER_DOCUMENTS?.POLSTURI?.length === 0) ||
        (chart?.OTHER_DOCUMENTS?.powerOfAttorneyExists && chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI?.length === 0) ? (
          <div className="mt-6 flex flex-col items-center">
            <p className="text-center text-2xl font-bold text-red-500 underline">ATTENTION:</p>
            <p className="text-center text-2xl font-bold text-red-500">YOUR APPLICATION IS MISSING DOCUMENTS</p>
            <p className="mt-4 text-center text-xl">
              You stated you have a{' '}
              {chart?.OTHER_DOCUMENTS?.livingWillExists
                ? 'living will'
                : chart?.OTHER_DOCUMENTS?.POLSTExists
                  ? 'POLST document'
                  : 'power of attorney document'}{' '}
              but have not yet uploaded it. You can{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => {
                  navigate({ to: `/${patientId}/upload/files` });
                }}
              >
                CLICK HERE
              </button>{' '}
              to upload it now. Or if you plan to email your documents to{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
              >
                info@mideohealth.com
              </button>{' '}
              at a later time, you can click the &apos;Submit Application&apos; button below.
            </p>
            <div className="mt-4">
              <button
                id={'nav-forward'}
                className="m-2 place-self-center rounded-md bg-red-900 px-4 py-2 text-white hover:bg-red-950 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
                type={'button'}
                onClick={handleSignOut}
              >
                Submit Application
              </button>
            </div>
          </div>
        ) : patientInfo?.applicationStatus === 'open' ? (
          <div className="flex flex-col items-center">
            <p className="text-center text-2xl font-bold text-green-500">YOUR APPLICATION IS READY FOR SUBMISSION</p>
            <p className="text-center text-2xl italic">Please click the button below to submit your application.</p>
            <button
              id={'nav-forward'}
              className="m-2 place-self-center rounded-md bg-red-900 px-4 py-2 text-white hover:bg-red-950 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
              type={'button'}
              onClick={handleSignOut}
            >
              Submit Application
            </button>
          </div>
        ) : (
          <>
            <p className="text-center text-3xl font-bold">Your application has been submitted successfully.</p>
          </>
        )}
        {(patientInfo.applicationStatus === 'submitted' &&
          chart?.OTHER_DOCUMENTS?.livingWillExists &&
          chart?.OTHER_DOCUMENTS?.livingWillURI?.length === 0) ||
        (chart?.OTHER_DOCUMENTS?.POLSTExists && chart?.OTHER_DOCUMENTS?.POLSTURI?.length === 0) ||
        (chart?.OTHER_DOCUMENTS?.powerOfAttorneyExists && chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI?.length === 0) ? (
          <div className="mt-4 flex flex-col items-center">
            <p className="text-center text-xl italic">
              Please remember to return to our patient portal to upload your legal documents or email them to{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
              >
                info@mideohealth.com
              </button>
            </p>
          </div>
        ) : (
          <>
            <p className="text-center text-2xl">
              If you have any questions or concerns, email us at{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
              >
                info@mideohealth.com
              </button>
            </p>
          </>
        )}
      </div>
    );
  }
}
